import { useEffect, useState } from "react";
import { Loader2, X } from "lucide-react";
import { PdfCanvasViewer } from "./pdf-canvas-viewer";
import { attachmentDownloadUrl, getToken } from "../lib/api";

export interface CloseoutDocViewerProps {
    /** Attachment to show. Fetched with auth; images render inline, PDFs render
        fit-to-width and scrollable via PdfCanvasViewer. */
    attachmentId: string;
    label?: string;
    /** mime_type from the document row; drives image vs PDF. Null → treat as PDF
        (the closeout docs are PDFs unless explicitly an image/*). */
    mime?: string | null;
    /** When the stage widens (Expand toggle) the pdf.js viewer must remount to
        re-fit the new width — its render effect keys on data only. */
    expanded?: boolean;
}

/**
 * The Documents-step doc stage viewer.
 *
 * Replaces the old raw <iframe> (browser native fit-to-PAGE viewer, which shrank
 * a letter-size report to an unreadable sliver). PDFs now render fit-to-WIDTH and
 * scrollable through the app's standard PdfCanvasViewer — the same viewer used by
 * the attachment/held-report/document preview dialogs — so the printed test
 * readings are legible without manual zoom. Images render inline, object-contain.
 */
export function CloseoutDocViewer({ attachmentId, label, mime, expanded }: CloseoutDocViewerProps) {
    const isImage = (mime ?? "").startsWith("image/");
    const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Images render straight from the (token-bearing) URL — no fetch needed.
        if (isImage) {
            setPdfData(null);
            setError(null);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        setPdfData(null);

        // attachmentDownloadUrl already carries the JWT as ?token= and is
        // origin-prefixed; the Bearer header is belt-and-suspenders (same shape
        // as attachment-preview-dialog) and must NOT re-wrap the URL.
        const token = getToken();
        const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
        fetch(attachmentDownloadUrl(attachmentId, { disposition: "inline" }), { headers })
            .then(async (res) => {
                if (!res.ok) throw new Error(`Couldn't load the document (${res.status})`);
                const buf = await res.arrayBuffer();
                if (!cancelled) setPdfData(buf);
            })
            .catch((err) => {
                if (!cancelled) setError(err instanceof Error ? err.message : "Couldn't load the document");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [attachmentId, isImage]);

    if (isImage) {
        return (
            <div className="flex h-full w-full items-center justify-center overflow-hidden">
                <img
                    src={attachmentDownloadUrl(attachmentId, { disposition: "inline" })}
                    alt={label ?? "Document"}
                    className="max-h-full max-w-full object-contain"
                />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-6 text-sm text-destructive">
                <X className="h-5 w-5" />
                <span>{error}</span>
                <span className="text-xs text-muted-foreground">
                    Use Edit document or Download to open it directly.
                </span>
            </div>
        );
    }

    if (loading || !pdfData) {
        return (
            <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading document…
            </div>
        );
    }

    return (
        <PdfCanvasViewer
            key={expanded ? "wide" : "narrow"}
            data={pdfData}
            className="relative flex h-full w-full flex-col"
        />
    );
}
